import cx from "clsx";
import { Collection } from "kreate-common/modules/business-types";
import { DisplayableError } from "kreate-common/modules/displayable-error";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import DropdownMenu from "kreate-common/modules/kreate-ui/components/DropdownMenu";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { useModalPromises } from "kreate-common/modules/kreate-ui/components/Modal/hoc/withModalPromises";
import { httpPost$LoveCollection } from "kreate-server/commands/LoveCollection/fetcher";
import { useRouter } from "next/router";
import React from "react";
import { FaCrown, FaFacebook } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import {
  MdDelete,
  MdEdit,
  MdFavorite,
  MdFavoriteBorder,
  MdMoreHoriz,
  MdShare,
  MdUpload,
} from "react-icons/md";

import { getPageTitle } from "../../../../utils";
import ModalRemoveCollection from "../../containers/ModalRemoveCollection";

import styles from "./index.module.scss";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import {
  shareOnFacebook,
  shareOnTwitter,
} from "@/modules/common-utils/shareOn";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import {
  getLinkToPageCollection,
  getLinkToPageRoyalties,
  getLinkToPageUpdateCollection,
} from "@/modules/navigation/utils/getLinkTo";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  collection: Collection | null | undefined;
};

export default function Toolbar({ className, style, collection }: Props) {
  const loginStatus = useLoginStatus();
  const router = useRouter();
  const { showModal } = useModalPromises();
  const { showError } = useToast();

  const isOwner =
    loginStatus?.loggedIn && loginStatus.userId === collection?.createdBy;
  const isLoved =
    loginStatus?.loggedIn && collection?.lovedBy?.includes(loginStatus.userId);

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Row
        flex="0 0 auto"
        alignItems="flex-start"
        gap="12px"
        flexWrap="wrap"
        justifyContent="flex-end"
      >
        {loginStatus?.loggedIn ? (
          <ButtonSecondary
            content="Love"
            icon={
              isLoved ? (
                <MdFavorite className={styles.colorFgPrimary} />
              ) : (
                <MdFavoriteBorder />
              )
            }
            onClick={async () => {
              try {
                await httpPost$LoveCollection(
                  `/api/v1/collection/${collection?.collectionId}/love`,
                  { love: !isLoved }
                );
                await shamelesslyRevalidateEverything();
              } catch (error) {
                const displayableError = DisplayableError.from(
                  error,
                  "Failed to send reaction"
                );
                showError({ error: displayableError });
              }
            }}
          />
        ) : null}
        {isOwner ? (
          <ButtonSecondary
            content="Upload Artwork"
            icon={<MdUpload />}
            onClick={() => {
              router.push(
                `/new/artwork?` +
                  new URLSearchParams({
                    initialCollectionId: collection.collectionId,
                  })
              );
            }}
          />
        ) : null}
        {isOwner ? (
          <ButtonSecondary
            icon={<FaCrown />}
            content="Set Royalties"
            onClick={async () =>
              await router.push(
                getLinkToPageRoyalties({
                  collectionId: collection.collectionId,
                })
              )
            }
          />
        ) : undefined}
        <DropdownMenu.Menu
          trigger={
            <ButtonSecondary
              icon={<MdShare />}
              size="md-square"
              title="Share"
            />
          }
        >
          <DropdownMenu.Item
            icon={<FaXTwitter />}
            content="Twitter"
            onSelect={
              collection
                ? () =>
                    shareOnTwitter(
                      getLinkToPageCollection(collection),
                      getPageTitle(collection)
                    )
                : undefined
            }
            disabled={!collection}
          />
          <DropdownMenu.Item
            icon={<FaFacebook />}
            content="Facebook"
            onSelect={
              collection
                ? () => shareOnFacebook(getLinkToPageCollection(collection))
                : undefined
            }
          />
        </DropdownMenu.Menu>
        {isOwner ? (
          <DropdownMenu.Menu
            trigger={
              <ButtonSecondary icon={<MdMoreHoriz />} size="md-square" />
            }
          >
            <DropdownMenu.Item
              icon={<MdEdit />}
              content="Edit Collection"
              onSelect={async () =>
                await router.push(getLinkToPageUpdateCollection(collection))
              }
              disabled={!collection}
            />
            <DropdownMenu.Item
              icon={<MdDelete />}
              content="Delete"
              onSelect={() => {
                showModal<void>(({ resolve }) => (
                  <ModalRemoveCollection
                    open
                    collection={collection}
                    onSuccess={async () => {
                      resolve(); // close modal first, then redirect
                      await router.push(`/user/${collection.createdBy}`);
                    }}
                    onClose={resolve}
                  />
                ));
              }}
              disabled={!collection}
            />
          </DropdownMenu.Menu>
        ) : null}
      </Flex.Row>
    </div>
  );
}
